import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { combineLatest, Observable } from 'rxjs';
import { selectCustomerSpaceNotifications, selectIsShowingNotifications } from '@core/core.selectors';
import { showNotifications } from '@core/core.actions';
import { Notification } from '@core/core.state';
import { filterSucceededResource } from '@platform/shared/resource.state';
import * as CoreActions from '@core/core.actions';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

const msPerDay = 86400000;
const msPerHour = 3600000;
const msPerMinute = 60000;

const showNotificationsQueryParamKey = 'showNotifications';

@Component({
  selector: 'app-notification-drawer',
  templateUrl: './notification-drawer.component.html',
  styleUrls: ['./notification-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationDrawerComponent {
  @Input({ required: true }) customerSpaceId: string | undefined;

  public isShowingNotifications$: Observable<boolean>;
  public notifications$: Observable<Notification[]>;

  constructor(
    private store$: Store<RootState>,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    this.isShowingNotifications$ = store$.pipe(select(selectIsShowingNotifications));
    this.notifications$ = store$.pipe(select(selectCustomerSpaceNotifications), filterSucceededResource());

    const showNotificationQueryParamValue$ = activatedRoute.queryParamMap.pipe(
      map((params) => params.get(showNotificationsQueryParamKey) === 'true'),
    );
    const customerSpaceId$ = activatedRoute.paramMap.pipe(map((params) => params.get('customerSpaceId')));

    combineLatest([showNotificationQueryParamValue$, customerSpaceId$])
      .pipe(takeUntilDestroyed())
      .subscribe(([showNotification, customerSpaceId]) => {
        if (showNotification && customerSpaceId) {
          store$.dispatch(showNotifications({ show: true }));
        }
      });
  }

  public async close() {
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [showNotificationsQueryParamKey]: undefined,
      },
      queryParamsHandling: 'merge',
    });

    this.store$.dispatch(showNotifications({ show: false }));
  }

  public getNotificationTime(notification: Notification): string {
    const today = new Date();
    const diffMs = today.getTime() - notification.triggeredOn.getTime();
    const diffDays = Math.floor(diffMs / msPerDay);
    const diffHrs = Math.floor((diffMs % msPerDay) / msPerHour);
    const diffMins = Math.round(((diffMs % msPerDay) % msPerHour) / msPerMinute);

    if (diffDays > 0) {
      if (diffDays === 1) {
        return this.translateService.instant('header.notifications.time-day');
      }

      const translation: string = this.translateService.instant('header.notifications.time-days');
      return translation.replace('$', diffDays.toString());
    }

    if (diffHrs > 0) {
      if (diffHrs === 1) {
        return this.translateService.instant('header.notifications.time-hour');
      }

      const translation: string = this.translateService.instant('header.notifications.time-hours');
      return translation.replace('$', diffHrs.toString());
    }

    if (diffMins > 0) {
      if (diffMins === 1) {
        return this.translateService.instant('header.notifications.time-minute');
      }

      const translation: string = this.translateService.instant('header.notifications.time-minutes');
      return translation.replace('$', diffMins.toString());
    }

    return this.translateService.instant('header.notifications.time-few-seconds');
  }

  public markAllAsRead() {
    if (this.customerSpaceId) {
      this.store$.dispatch(
        CoreActions.markAllNotificationsAsRead({
          customerSpaceId: this.customerSpaceId,
        }),
      );
    }
  }

  public markAsRead(notification: Notification) {
    this.store$.dispatch(
      CoreActions.markNotificationAsRead({
        customerSpaceId: notification.customerSpaceId,
        notificationId: notification.id,
      }),
    );
  }

  public markAsUnread(notification: Notification) {
    this.store$.dispatch(
      CoreActions.markNotificationAsUnread({
        customerSpaceId: notification.customerSpaceId,
        notificationId: notification.id,
      }),
    );
  }

  public getNotificationTriggeredByName(notification: Notification): string {
    if (notification.triggeredByFallbackName === 'TaskOverdue') {
      return '';
    } else if (notification.triggeredByName === '') {
      return notification.triggeredByFallbackName + ' ';
    } else {
      return notification.triggeredByName + ' ';
    }
  }

  public getRouterLink(notification: Notification): string[] {
    switch (notification.type) {
      case 'CustomerSpaceAssignment':
        return ['/', 'customer-spaces', notification.customerSpaceId, 'cockpit'];
      case 'PortalTask':
      case 'PortalTaskComment':
        return ['/', 'customer-spaces', notification.customerSpaceId, 'fiduciary', 'tasks', notification.itemId];
      case 'DocumentComment':
        return ['/', 'customer-spaces', notification.customerSpaceId, 'documents'];
      case 'FileProcessAssignment':
        return ['/'].concat(notification.directLink.split('/'));
    }

    return [];
  }
}
